import { useEffect, useMemo, useState } from 'react';
import { createSalesOrderFormInitialValues } from '../data/salesOrderConstants';
import * as defaultSalesOrderService from '../services/salesOrderService';
import { refreshNavigationNotifications } from './useNavigationNotifications';

const DEFAULT_SALES_ORDER_STATUS_FILTER = 'ActivePartiallyReleasedCompletedUnpaid';
const SALES_ORDER_PAGE_SIZE = 10;

function useSalesOrders({
  includeContainerDetails = true,
  includeWarehouseLocation = true,
  includeCustomerBranch = false,
  includeSeaweedType = false,
  includeCommercialFields = false,
  service = defaultSalesOrderService,
} = {}) {
  const [activeView, setActiveView] = useState(getInitialSalesOrderActiveView);
  const [filterValues, setFilterValues] = useState(createInitialSalesOrderFilterValues);
  const [appliedFilters, setAppliedFilters] = useState(createInitialSalesOrderFilterValues);
  const [currentPage, setCurrentPage] = useState(1);
  const [salesOrders, setSalesOrders] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [seaweedTypeOptions, setSeaweedTypeOptions] = useState([]);
  const [containerOptions, setContainerOptions] = useState([]);
  const [formValues, setFormValues] = useState(createSalesOrderFormInitialValues);
  const [formErrors, setFormErrors] = useState({});
  const [editingSalesOrder, setEditingSalesOrder] = useState(null);
  const [editValues, setEditValues] = useState(createSalesOrderFormInitialValues);
  const [editErrors, setEditErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditSaving, setIsEditSaving] = useState(false);
  const [isBillingSaving, setIsBillingSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [editError, setEditError] = useState('');
  const [billingError, setBillingError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage('');
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [successMessage]);

  useEffect(() => {
    let isMounted = true;

    async function loadSalesOrderPageData() {
      setIsLoading(true);
      setLoadError('');

      try {
        const [ordersResponse, formOptionsResponse] = await Promise.all([
          service.fetchSalesOrders(),
          service.fetchSalesOrderFormOptions(),
        ]);

        if (!isMounted) {
          return;
        }

        const activeCustomerOptions = getActiveCustomerOptions(formOptionsResponse.customers);

        setSalesOrders(ordersResponse);
        setCustomerOptions(activeCustomerOptions);
        setSeaweedTypeOptions(formOptionsResponse.seaweedTypes || []);
        setContainerOptions(formOptionsResponse.seaweedsContainers);
        setFormValues((currentValues) =>
          applyFormDefaults(
            currentValues,
            activeCustomerOptions,
            formOptionsResponse.seaweedTypes || [],
            formOptionsResponse.seaweedsContainers,
            includeContainerDetails,
            includeCustomerBranch,
            includeSeaweedType
          )
        );
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadSalesOrderPageData();

    return () => {
      isMounted = false;
    };
  }, [includeContainerDetails, includeCustomerBranch, includeSeaweedType, service]);

  const canCreate =
    customerOptions.length > 0 &&
    (includeContainerDetails ? containerOptions.length > 0 : true) &&
    (includeSeaweedType ? seaweedTypeOptions.length > 0 : true);

  const selectedCustomer = useMemo(
    () => getCustomerById(customerOptions, formValues.customerId),
    [customerOptions, formValues.customerId]
  );

  const selectedEditCustomer = useMemo(
    () => getCustomerById(customerOptions, editValues.customerId),
    [customerOptions, editValues.customerId]
  );

  const customerBranchOptions = useMemo(
    () =>
      normalizeCustomerBranches(selectedCustomer).map((branch) => ({
        value: branch,
        label: branch,
      })),
    [selectedCustomer]
  );

  const editCustomerBranchOptions = useMemo(
    () =>
      normalizeCustomerBranches(selectedEditCustomer).map((branch) => ({
        value: branch,
        label: branch,
      })),
    [selectedEditCustomer]
  );

  const selectedContainer = useMemo(
    () =>
      containerOptions.find((option) => option.value === formValues.sourcePurchaseOrderId) || null,
    [containerOptions, formValues.sourcePurchaseOrderId]
  );

  const selectedEditContainer = useMemo(
    () =>
      containerOptions.find((option) => option.value === editValues.sourcePurchaseOrderId) || null,
    [containerOptions, editValues.sourcePurchaseOrderId]
  );

  const filteredSalesOrders = useMemo(() => {
    const normalizedSearch = String(appliedFilters.search || '').trim().toLowerCase();

    return salesOrders.filter((salesOrder) => {
      const soCode = getSalesOrderSearchableCode(salesOrder).toLowerCase();
      const customerName = String(salesOrder.customerName || '').toLowerCase();
      const matchesSearch =
        !normalizedSearch || soCode.includes(normalizedSearch) || customerName.includes(normalizedSearch);
      const matchesSoDate =
        !appliedFilters.soDate || toDateInputValue(salesOrder.soDate) === appliedFilters.soDate;
      const matchesStatus =
        appliedFilters.statusFilter === 'All' ||
        (appliedFilters.statusFilter === 'ActiveAndPartiallyReleased'
          ? ['Active', 'Partially Released'].includes(String(salesOrder.status || ''))
          : false) ||
        (appliedFilters.statusFilter === 'ActivePartiallyReleasedCompletedUnpaid'
          ? isActivePartiallyReleasedOrCompletedUnpaidSalesOrder(salesOrder)
          : false) ||
        String(salesOrder.status || '') === appliedFilters.statusFilter;

      return matchesSearch && matchesSoDate && matchesStatus;
    });
  }, [appliedFilters, salesOrders]);

  const sortedSalesOrders = useMemo(
    () =>
      [...filteredSalesOrders].sort((firstOrder, secondOrder) =>
        secondOrder.soDate.localeCompare(firstOrder.soDate)
      ),
    [filteredSalesOrders]
  );
  const salesOrderSearchSuggestions = useMemo(
    () => buildSalesOrderSearchSuggestions(salesOrders),
    [salesOrders]
  );
  const salesOrderStatusOptions = useMemo(() => {
    const statusValues = Array.from(
      new Set(
        ['Active', 'Partially Released', 'Completed', 'Cancelled', ...salesOrders
          .map((salesOrder) => String(salesOrder.status || '').trim())
          .filter(Boolean)]
      )
    ).sort((firstStatus, secondStatus) => firstStatus.localeCompare(secondStatus));

    return statusValues.map((statusValue) => ({
      value: statusValue,
      label: statusValue,
    }));
  }, [salesOrders]);
  const totalSalesOrders = sortedSalesOrders.length;
  const totalPages = Math.max(1, Math.ceil(totalSalesOrders / SALES_ORDER_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * SALES_ORDER_PAGE_SIZE;
  const endIndex = Math.min(startIndex + SALES_ORDER_PAGE_SIZE, totalSalesOrders);
  const paginatedSalesOrders = useMemo(
    () => sortedSalesOrders.slice(startIndex, endIndex),
    [endIndex, sortedSalesOrders, startIndex]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters]);

  function handleFormChange(event) {
    const { name } = event.target;
    const value = getNormalizedInput(name, event.target.value);

    setFormValues((currentValues) => {
      const nextValues = {
        ...currentValues,
        [name]: value,
      };

      if (name === 'customerId' && includeCustomerBranch) {
        nextValues.branch = '';
      }

      return nextValues;
    });
    setFormErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
      ...(name === 'customerId' ? { branch: '' } : null),
    }));
    setSubmitError('');
    setSuccessMessage('');
  }

  function handleFilterChange(event) {
    const { name, value } = event.target;

    setFilterValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    if (name === 'search') {
      setAppliedFilters((currentFilters) => ({
        ...currentFilters,
        search: value,
      }));
      setCurrentPage(1);
    }
  }

  function handleFilterSubmit(event) {
    event.preventDefault();
    setAppliedFilters(filterValues);
    setCurrentPage(1);
  }

  function handleStatusFilterChange(event) {
    const { value } = event.target;

    setFilterValues((currentValues) => ({
      ...currentValues,
      statusFilter: value,
    }));
    setAppliedFilters((currentFilters) => ({
      ...currentFilters,
      statusFilter: value,
    }));
    setCurrentPage(1);
  }

  function handleFilterReset() {
    const resetValues = createInitialSalesOrderFilterValues();
    setFilterValues(resetValues);
    setAppliedFilters(resetValues);
    setCurrentPage(1);
  }

  function handlePreviousPage() {
    setCurrentPage((page) => Math.max(page - 1, 1));
  }

  function handleNextPage() {
    setCurrentPage((page) => Math.min(page + 1, totalPages));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = validateSalesOrderForm(
      formValues,
      includeContainerDetails,
      includeWarehouseLocation,
      includeCustomerBranch,
      includeSeaweedType,
      includeCommercialFields
    );

    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const createdSalesOrder = await service.createSalesOrder(
        normalizeSalesOrderPayload(
          formValues,
          includeContainerDetails,
          includeWarehouseLocation,
          includeCustomerBranch,
          includeSeaweedType,
          includeCommercialFields
        )
      );
      setSalesOrders((currentOrders) => [createdSalesOrder, ...currentOrders]);
      resetForm();
      setActiveView('list');
      setSuccessMessage('Sales order added successfully.');
      refreshNavigationNotifications();
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    resetForm();
    setActiveView('list');
  }

  function openEditModal(salesOrder) {
    setEditingSalesOrder(salesOrder);
    setEditValues({
      customerId: String(salesOrder.customerId),
      branch: salesOrder.branch || '',
      seaweedType: salesOrder.seaweedType || '',
      sourcePurchaseOrderId: String(salesOrder.sourcePurchaseOrderId || ''),
      fclBatchNumber: salesOrder.fclBatchNumber || '',
      codeNumber: salesOrder.codeNumber || '',
      containerNumber: salesOrder.containerNumber || '',
      quantity: String(salesOrder.quantity ?? ''),
      dealPrice: String(salesOrder.dealPrice ?? ''),
      soDate: salesOrder.soDate,
      warehouseLocation: salesOrder.warehouseLocation || '',
    });
    setEditErrors({});
    setEditError('');
    setSuccessMessage('');
  }

  function closeEditModal() {
    setEditingSalesOrder(null);
    setEditValues(createSalesOrderFormInitialValues());
    setEditErrors({});
    setEditError('');
  }

  function handleEditChange(event) {
    const { name } = event.target;
    const value = getNormalizedInput(name, event.target.value);

    setEditValues((currentValues) => {
      const nextValues = {
        ...currentValues,
        [name]: value,
      };

      if (name === 'customerId' && includeCustomerBranch) {
        const nextCustomer = getCustomerById(customerOptions, value);
        const nextBranches = normalizeCustomerBranches(nextCustomer);
        nextValues.branch = nextBranches[0] || '';
      }

      return nextValues;
    });
    setEditErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
      ...(name === 'customerId' ? { branch: '' } : null),
    }));
    setEditError('');
    setSuccessMessage('');
  }

  async function handleEditSubmit(event) {
    event.preventDefault();

    if (!editingSalesOrder) {
      return false;
    }

    const nextErrors = validateSalesOrderForm(
      editValues,
      includeContainerDetails,
      includeWarehouseLocation,
      includeCustomerBranch,
      includeSeaweedType,
      includeCommercialFields
    );

    if (Object.keys(nextErrors).length) {
      setEditErrors(nextErrors);
      return false;
    }

    setIsEditSaving(true);
    setEditError('');

    try {
      const updatedSalesOrder = await service.updateSalesOrder(
        editingSalesOrder.id,
        normalizeSalesOrderPayload(
          editValues,
          includeContainerDetails,
          includeWarehouseLocation,
          includeCustomerBranch,
          includeSeaweedType,
          includeCommercialFields
        )
      );

      setSalesOrders((currentOrders) =>
        currentOrders.map((salesOrder) =>
          salesOrder.id === editingSalesOrder.id ? updatedSalesOrder : salesOrder
        )
      );
      closeEditModal();
      setSuccessMessage('Sales order updated successfully.');
      refreshNavigationNotifications();
      return true;
    } catch (error) {
      setEditError(error.message);
      return false;
    } finally {
      setIsEditSaving(false);
    }
  }

  async function handleDelete(salesOrder) {
    if (!salesOrder || typeof service.deleteSalesOrder !== 'function') {
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const cancelledSalesOrder = await service.deleteSalesOrder(salesOrder.id);
      setSalesOrders((currentOrders) =>
        currentOrders.map((currentOrder) =>
          currentOrder.id === cancelledSalesOrder.id ? cancelledSalesOrder : currentOrder
        )
      );
      setSuccessMessage('Sales order cancelled successfully.');
      refreshNavigationNotifications();
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleReopen(salesOrder) {
    if (!salesOrder || typeof service.reopenSalesOrder !== 'function') {
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const reopenedSalesOrder = await service.reopenSalesOrder(salesOrder.id);
      setSalesOrders((currentOrders) =>
        currentOrders.map((currentOrder) =>
          currentOrder.id === reopenedSalesOrder.id ? reopenedSalesOrder : currentOrder
        )
      );
      setSuccessMessage('Sales order reopened successfully.');
      refreshNavigationNotifications();
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleBillingSubmit(salesOrder, payload) {
    if (!salesOrder || typeof service.updateSalesOrderBilling !== 'function') {
      return false;
    }

    setIsBillingSaving(true);
    setBillingError('');
    setSuccessMessage('');

    try {
      const updatedSalesOrder = await service.updateSalesOrderBilling(salesOrder.id, payload);

      setSalesOrders((currentOrders) =>
        currentOrders.map((currentOrder) =>
          currentOrder.id === updatedSalesOrder.id ? updatedSalesOrder : currentOrder
        )
      );
      setSuccessMessage('Customer payment status updated successfully.');
      return true;
    } catch (error) {
      setBillingError(error.message);
      return false;
    } finally {
      setIsBillingSaving(false);
    }
  }

  function resetForm() {
    setFormValues(
      applyFormDefaults(
        createSalesOrderFormInitialValues(),
        customerOptions,
        seaweedTypeOptions,
        containerOptions,
        includeContainerDetails,
        includeCustomerBranch,
        includeSeaweedType
      )
    );
    setFormErrors({});
    setSubmitError('');
  }

  return {
    activeView,
    filterValues,
    salesOrders: paginatedSalesOrders,
    salesOrderPagination: {
      currentPage: normalizedPage,
      endItem: endIndex,
      isNextDisabled: normalizedPage >= totalPages,
      isPreviousDisabled: normalizedPage <= 1,
      onNext: handleNextPage,
      onPrevious: handlePreviousPage,
      startItem: totalSalesOrders ? startIndex + 1 : 0,
      totalItems: totalSalesOrders,
      totalPages,
    },
    salesOrderSearchSuggestions,
    salesOrderStatusOptions,
    customerOptions,
    seaweedTypeOptions,
    containerOptions,
    customerBranchOptions,
    editCustomerBranchOptions,
    selectedContainer,
    selectedEditContainer,
    formValues,
    formErrors,
    editingSalesOrder,
    editValues,
    editErrors,
    isLoading,
    isSaving,
    isEditSaving,
    isBillingSaving,
    loadError,
    submitError,
    editError,
    billingError,
    successMessage,
    canCreate,
    setActiveView,
    handleFilterChange,
    handleFilterSubmit,
    handleStatusFilterChange,
    handleFilterReset,
    handleFormChange,
    handleSubmit,
    handleCancel,
    openEditModal,
    closeEditModal,
    handleEditChange,
    handleEditSubmit,
    handleDelete,
    handleReopen,
    handleBillingSubmit,
  };
}

function createInitialSalesOrderFilterValues() {
  return {
    search: '',
    soDate: '',
    statusFilter: DEFAULT_SALES_ORDER_STATUS_FILTER,
  };
}

function getInitialSalesOrderActiveView() {
  return 'list';
}

function isActivePartiallyReleasedOrCompletedUnpaidSalesOrder(salesOrder) {
  const status = String(salesOrder.status || '');
  const billingStatus = String(salesOrder.billingStatus || 'Unpaid');

  return (
    ['Active', 'Partially Released'].includes(status) ||
    (status === 'Completed' && billingStatus !== 'Paid')
  );
}

function validateSalesOrderForm(
  values,
  includeContainerDetails,
  includeWarehouseLocation,
  includeCustomerBranch,
  includeSeaweedType,
  includeCommercialFields
) {
  const errors = {};

  if (!values.customerId) {
    errors.customerId = 'Customer is required.';
  }

  if (includeCustomerBranch && !values.branch?.trim()) {
    errors.branch = 'Delivery location is required.';
  }

  if (includeSeaweedType && !values.seaweedType) {
    errors.seaweedType = 'Seaweed type is required.';
  }

  if (includeContainerDetails) {
    if (!values.sourcePurchaseOrderId) {
      errors.sourcePurchaseOrderId = 'Seaweeds container is required.';
    }

    if (!values.fclBatchNumber.trim()) {
      errors.fclBatchNumber = 'FCL batch number is required.';
    }

    if (!values.codeNumber.trim()) {
      errors.codeNumber = 'Code number is required.';
    }

    if (!values.containerNumber.trim()) {
      errors.containerNumber = 'Container number is required.';
    }
  }

  if (!values.soDate) {
    errors.soDate = 'S.O. date is required.';
  }

  if (includeWarehouseLocation && !values.warehouseLocation.trim()) {
    errors.warehouseLocation = 'Warehouse location or branch is required.';
  }

  if (includeCommercialFields) {
    const quantity = Number(values.quantity);

    if (!Number.isFinite(quantity) || quantity <= 0) {
      errors.quantity = 'FCL quantity must be greater than zero.';
    }

  }

  return errors;
}

function normalizeSalesOrderPayload(
  values,
  includeContainerDetails,
  includeWarehouseLocation,
  includeCustomerBranch,
  includeSeaweedType,
  includeCommercialFields
) {
  const payload = {
    customerId: Number(values.customerId),
    soDate: values.soDate,
  };

  if (includeCustomerBranch) {
    payload.branch = values.branch.trim();
  }

  if (includeSeaweedType) {
    payload.seaweedType = String(values.seaweedType || '').trim();
  }

  if (includeCommercialFields) {
    payload.quantity = Number(values.quantity);
    payload.dealPrice = Number(values.dealPrice || 0);
  }

  if (includeContainerDetails) {
    payload.sourcePurchaseOrderId = Number(values.sourcePurchaseOrderId);
    payload.fclBatchNumber = values.fclBatchNumber.trim();
    payload.codeNumber = values.codeNumber.trim();
    payload.containerNumber = values.containerNumber.trim();
  }

  if (includeWarehouseLocation) {
    payload.warehouseLocation = values.warehouseLocation.trim();
  }

  return payload;
}

function applyFormDefaults(
  values,
  customerOptions,
  seaweedTypeOptions,
  containerOptions,
  includeContainerDetails,
  includeCustomerBranch,
  includeSeaweedType
) {
  const nextValues = { ...values };

  if (!nextValues.customerId && customerOptions.length === 1) {
    nextValues.customerId = customerOptions[0].value;
  }

  if (includeCustomerBranch) {
    const selectedCustomer = getCustomerById(customerOptions, nextValues.customerId);
    const branches = normalizeCustomerBranches(selectedCustomer);

    if (!branches.includes(nextValues.branch)) {
      nextValues.branch = '';
    }
  }

  if (includeSeaweedType && !nextValues.seaweedType && seaweedTypeOptions.length === 1) {
    nextValues.seaweedType = seaweedTypeOptions[0].value;
  }

  if (
    includeContainerDetails &&
    !nextValues.sourcePurchaseOrderId &&
    containerOptions.length > 0
  ) {
    nextValues.sourcePurchaseOrderId = containerOptions[0].value;
  }

  return nextValues;
}

function getCustomerById(customerOptions, customerId) {
  return (
    customerOptions.find((customerOption) => customerOption.value === String(customerId)) || null
  );
}

function getActiveCustomerOptions(customerOptions = []) {
  return customerOptions.filter(
    (customerOption) => String(customerOption.status || 'Active').trim() === 'Active'
  );
}

function normalizeCustomerBranches(customerOption) {
  if (!customerOption || !Array.isArray(customerOption.branches)) {
    return [];
  }

  return customerOption.branches
    .map((branch) => String(branch || '').trim())
    .filter(Boolean);
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  return String(value).slice(0, 10);
}

function getSalesOrderSearchableCode(salesOrder) {
  if (salesOrder.soCode) {
    return String(salesOrder.soCode);
  }

  return `B-${String(salesOrder.id).padStart(3, '0')}`;
}

function buildSalesOrderSearchSuggestions(salesOrders) {
  const suggestionsByKey = new Map();

  salesOrders.forEach((salesOrder) => {
    const soCode = getSalesOrderSearchableCode(salesOrder);
    const customerName = String(salesOrder.customerName || '').trim();

    if (soCode) {
      suggestionsByKey.set(`so-code:${soCode.toLowerCase()}`, {
        group: 'so-code',
        value: soCode,
        label: soCode,
        detail: customerName ? `Batch No. - ${customerName}` : 'Batch No.',
        status: salesOrder.status || '',
      });
    }

    if (customerName) {
      suggestionsByKey.set(`customer:${customerName.toLowerCase()}`, {
        group: 'customer',
        value: customerName,
        label: customerName,
        detail: 'Customer',
        status: salesOrder.status || '',
      });
    }
  });

  return Array.from(suggestionsByKey.values()).sort((firstSuggestion, secondSuggestion) =>
    firstSuggestion.label.localeCompare(secondSuggestion.label)
  );
}

function getNormalizedInput(name, value) {
  if (name === 'quantity') {
    return normalizeQuantityInput(value);
  }

  return value;
}

function normalizeQuantityInput(value) {
  const rawValue = String(value ?? '');
  const sanitizedValue = rawValue.replace(/[^\d.]/g, '');

  if (!sanitizedValue) {
    return '';
  }

  const [wholePartRaw = '', ...decimalParts] = sanitizedValue.split('.');
  const normalizedWholePart = wholePartRaw.replace(/^0+(?=\d)/, '');

  if (!decimalParts.length) {
    return normalizedWholePart;
  }

  const decimalPart = decimalParts.join('').slice(0, 2);
  return `${normalizedWholePart || '0'}.${decimalPart}`;
}

export default useSalesOrders;
